// Auto-renew on a sold membership (memberships.setAutoRenew, F-27). On: the plan's renewal terms
// (memberships.renewalTerms) are shown word for word and sent back with the customer's yes, exactly as
// a sale records it; never a bare switch. Off: confirmed, then recorded. The membership sheet takes
// the server's renewal as it comes back, so "Renews to" (the card) follows without a re-read.

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { errorText } from '../books/ui.tsx';
import { SaveButton } from '../shared/SaveButton.tsx';
import { autoRenewSaid, offOutcome, type RenewalWithCopy } from './auto-renew-model.ts';
import { renewalConsent, type MembershipDetail, type RenewalTerms } from './model.ts';

export function AutoRenew({
  detail: d,
  membership,
  onChanged,
}: {
  detail: MembershipDetail;
  /** Whose it is, for the button's name: "Fairview residence’s Comfort Club membership". */
  membership: string;
  onChanged: (next: RenewalWithCopy) => void;
}) {
  const on = !!d.renewal?.on;
  const [open, setOpen] = useState(false);
  const [terms, setTerms] = useState<RenewalTerms | null>(null);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    if (!open || on || terms) return;
    let current = true;
    void client
      .call<RenewalTerms>('memberships.renewalTerms', { typeId: d.typeId })
      .then((t) => current && setTerms(t))
      .catch((e: unknown) => current && setTermsError(errorText(e, 'Could not load the renewal terms.')));
    return () => {
      current = false;
    };
  }, [open, on, terms, d.typeId]);
  const consent = renewalConsent(!on, terms, agreed);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await client.call<{ renewal: RenewalWithCopy }>('memberships.setAutoRenew', {
        membershipId: d.id,
        on: !on,
        ...(consent.consent && !on ? { renewalConsent: consent.consent } : {}),
      });
      setSaid(autoRenewSaid(!on, r.renewal));
      setOpen(false);
      setAgreed(false);
      onChanged(r.renewal);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auto-renew">
      {said && (
        <p className="small" role="status">
          {said}
        </p>
      )}
      {!open ? (
        <button
          type="button"
          className="btn quiet sm"
          aria-label={`${on ? 'Turn auto-renew off' : 'Turn auto-renew on'} for ${membership}…`}
          onClick={() => {
            setSaid(null);
            setOpen(true);
          }}
        >
          {on ? 'Turn auto-renew off…' : 'Turn auto-renew on…'}
        </button>
      ) : on ? (
        <div className="renewal-terms" role="group" aria-label="Turn auto-renew off">
          <p className="small">{offOutcome}</p>
          <div className="row">
            <button type="button" className="btn soft" onClick={() => setOpen(false)}>
              Keep it on
            </button>
            <SaveButton busy={busy} busyLabel="Turning it off…" error={error} onClick={() => void save()}>
              Turn auto-renew off
            </SaveButton>
          </div>
        </div>
      ) : (
        <div className="renewal-terms" role="group" aria-label="Renewal terms">
          {termsError ? (
            <p className="error small">{termsError}</p>
          ) : !terms ? (
            <p className="muted small">Getting the renewal terms…</p>
          ) : (
            <>
              <p className="small renewal-text">{terms.text}</p>
              <label className="checkrow">
                <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} /> The
                customer agrees to these terms
              </label>
            </>
          )}
          <div className="row">
            <button type="button" className="btn soft" onClick={() => setOpen(false)}>
              Cancel
            </button>
            <SaveButton
              busy={busy}
              busyLabel="Turning it on…"
              error={error}
              disabled={!consent.ready || !terms}
              onClick={() => void save()}
            >
              Turn auto-renew on
            </SaveButton>
          </div>
        </div>
      )}
    </div>
  );
}
